// Confirm dialog — guard for destructive actions (reset, back-to-series).
//
// Owned by SceneDesignerTool: actions request a confirm via host.openConfirm,
// the tool stores the payload in state and renders this component until the
// user accepts or cancels. The dialog itself is a thin wrapper with no own
// state; it just calls back into the host on either decision.
//
// Visual: matches the single-menu cart modal at a smaller size so it
// reads as part of the same surface vocabulary. Tokens fall through to
// neutral defaults when the layout doesn't define them.

import { useEffect } from "react";

export interface ConfirmDialogProps {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  // Esc to cancel. The action layer treats Esc as a soft "no", same as
  // clicking the backdrop or the Cancel button — never a confirm.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCancel();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="sd-confirm-backdrop" onClick={onCancel}>
      <div
        className="sd-confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={title ? "sd-confirm-title" : undefined}
        onClick={e => e.stopPropagation()}
      >
        {title && (
          <h2 id="sd-confirm-title" className="sd-confirm__title">
            {title}
          </h2>
        )}
        <p className="sd-confirm__message">{message}</p>
        <div className="sd-confirm__actions">
          <button
            type="button"
            className="sd-confirm__btn sd-confirm__btn--ghost"
            onClick={onCancel}
            autoFocus
          >
            {cancelLabel ?? "Cancel"}
          </button>
          <button
            type="button"
            className="sd-confirm__btn sd-confirm__btn--primary"
            onClick={onConfirm}
          >
            {confirmLabel ?? "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
